import { useActiveWorkspaceRole } from "../../../header/useActiveWorkspaceRole";
import type { PolicyApprovalState } from "../services/boostPolicyService";

/**
 * Policy approval workflow hook.
 *
 * Gates the Save/Export controls when the POLICY_APPROVAL_REQUIRED feature flag
 * is on: members must request org_admin approval before a generated policy can be
 * saved as completed or exported. Org admins bypass the gate entirely.
 *
 * The flag is read once per module load (env is static at build/runtime); the role
 * is live (updates on workspace switch via useActiveWorkspaceRole).
 *
 * Approval *state* (pending/approved/rejected) is per-policy, so it's derived from
 * the policy record via getApprovalState() in boostPolicyService and passed into
 * the gate helpers below — not held in this hook.
 */

// Read the flag inline (matches the import.meta.env pattern used across revisr
// services). Defaults to false when unset — current workflow unchanged.
export const POLICY_APPROVAL_REQUIRED =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_POLICY_APPROVAL_REQUIRED === 'true');

export type UsePolicyApprovalResult = {
  /** Feature flag value (env-driven, static for the session). */
  approvalRequired: boolean;
  /** Whether the active user is an org admin (live, workspace-aware). */
  isOrgAdmin: boolean;
  /** Role still loading (degrade open — default to not-gated while loading). */
  isLoading: boolean;
};

export const usePolicyApproval = (): UsePolicyApprovalResult => {
  const { isOrgAdmin, isLoading } = useActiveWorkspaceRole();
  return {
    approvalRequired: POLICY_APPROVAL_REQUIRED,
    isOrgAdmin,
    isLoading,
  };
};

/**
 * True when the approval gate is *passed* for this viewer + policy — i.e. the
 * viewer may save as completed / export. Returns true when the flag is off, the
 * viewer is an org admin, or the policy is already approved.
 */
export const isApprovalGatePassed = (
  approvalRequired: boolean,
  isOrgAdmin: boolean,
  state: PolicyApprovalState,
): boolean => {
  if (!approvalRequired || isOrgAdmin) return true;
  return state === 'approved';
};